import React, { useEffect, useState } from 'react';
import { fetchProfitAnalysis, fetchProfitAnalysisDetail } from '../services/api';

/**
 * Reports > Profit Analysis: revenue, profit, margin and shipping cost by shipping type for a ship
 * date range, with VIP and non-VIP behind each type. The admin portal report on the SQL01 copy
 * (Azure exit WS8).
 */

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (a, b) => (Number(b) ? `${((Number(a) / Number(b)) * 100).toFixed(2)}%` : '');

function Cells({ r }) {
  return (
    <>
      <td>{r.countOfOrders}</td>
      <td>{money(r.totalAmount)}</td>
      <td>{Number(r.countOfOrders) ? money(Number(r.totalAmount) / Number(r.countOfOrders)) : ''}</td>
      <td>{money(r.orderProfit)}</td>
      <td>{pct(r.orderProfit, r.totalAmount)}</td>
      <td>{money(r.totalShipCost)}</td>
      <td>{money(r.ourShipCost)}</td>
    </>
  );
}

export default function ProfitAnalysis() {
  const [from, setFrom] = useState(daysAgo(365));
  const [to, setTo] = useState(daysAgo(0));
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState({});

  useEffect(() => {
    let live = true;
    setRows(null);
    setError(null);
    setOpen(null);
    setDetail({});
    fetchProfitAnalysis(from, to)
      .then((r) => { if (live) setRows(r); })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [from, to]);

  const toggle = (method) => {
    if (open === method) { setOpen(null); return; }
    setOpen(method);
    if (!detail[method]) {
      fetchProfitAnalysisDetail(from, to, method)
        .then((r) => setDetail((d) => ({ ...d, [method]: r })))
        .catch((e) => setError(e.message));
    }
  };

  const sum = (key) => (rows || []).reduce((s, r) => s + Number(r[key] || 0), 0);

  return (
    <div>
      <div className="page-header">
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Profit Analysis</h2>
      </div>
      <div className="port-filters">
        <input type="date" className="form-input" style={{ width: 'auto' }} value={from} onChange={(e) => setFrom(e.target.value)} />
        <input type="date" className="form-input" style={{ width: 'auto' }} value={to} onChange={(e) => setTo(e.target.value)} />
      </div>
      <div className="card">
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!rows && !error && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {rows && rows.length === 0 && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No orders in this range.</div>}
        {rows && rows.length > 0 && (
          <div className="port-scroll">
            <table className="trans-table">
              <thead>
                <tr><th>Shipping Type</th><th>Orders</th><th>Revenue</th><th>Avg Order</th><th>Profit</th><th>Margin</th><th>Shipping Cost</th><th>Ship Charged</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <React.Fragment key={r.shippingTypeName || 'none'}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => toggle(r.shippingTypeName)}>
                      <td style={{ color: 'var(--orange)', fontWeight: 600 }}>{r.shippingTypeName || '(none)'}</td>
                      <Cells r={r} />
                    </tr>
                    {open === r.shippingTypeName && (detail[r.shippingTypeName] || []).map((d) => (
                      <tr key={d.customerType} style={{ background: 'var(--gray-50)' }}>
                        <td style={{ paddingLeft: 24 }}>{d.customerType}</td>
                        <Cells r={d} />
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                <tr style={{ fontWeight: 700 }}>
                  <td>Total</td>
                  <Cells r={{ countOfOrders: sum('countOfOrders'), totalAmount: sum('totalAmount'), orderProfit: sum('orderProfit'), totalShipCost: sum('totalShipCost'), ourShipCost: sum('ourShipCost') }} />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}